import React, { useCallback, useState } from "react";
import Link from "next/link";
import { useSelector } from "react-redux";

import { addFollower } from "api/requests/addFollower";
import { removeFollower } from "api/requests/removeFollower";
import { Action, Category, Label } from "constants/events";
import { useSnackBar } from "context/SnackBarContext";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { getIsLoggedIn } from "redux/selectors/user";

import Button from "components/botanic/Button";
import NotFoundIcon from "components/Icons/not_found.svg";
import SsoBumpoutModal from "components/SsoBumpoutModal";

const ShopNoResults: React.FC<{
  titleText: string;
  subtitleText?: string;
  buttonText: string;
  onButtonClick?: () => void;
  brandId?: number;
  isFollowing?: boolean;
  linkText?: string;
  linkHref?: string;
  wrapperClassName?: string;
}> = ({
  titleText,
  subtitleText,
  buttonText,
  onButtonClick,
  brandId,
  isFollowing,
  linkText,
  linkHref,
  wrapperClassName,
}) => {
  const { publishEvent } = useEventTracker();
  const { addSnackBarItem } = useSnackBar();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const countryCode = useDomainCountryCode();
  const [isSsoModalVisible, setIsSsoModalVisible] = useState(false);
  const [brandFavorited, setBrandIsFavorited] = useState(isFollowing);

  const handleFavoriteClick = useCallback(async () => {
    if (brandId) {
      if (!isLoggedIn) {
        return setIsSsoModalVisible(true);
      }
      publishEvent({
        action: Action.click,
        brandId: brandId,
        category: Category.brandPaid,
        label: brandFavorited ? Label.unfollowButton : Label.followButton,
      });

      const followerCall = brandFavorited ? removeFollower : addFollower;

      const success = await followerCall({
        countryCode,
        followeeId: brandId,
        followeeType: "brand",
      });

      if (success) {
        setBrandIsFavorited(!brandFavorited);
        addSnackBarItem(
          followerCall === addFollower
            ? "brandFavoriteAdded"
            : "brandFavoriteRemoved",
        );
      }
    }
  }, [brandFavorited, isLoggedIn]);
  return (
    <div className={wrapperClassName}>
      <div className="border border-light-grey rounded flex flex-col items-center gap-lg p-lg">
        <div className="flex flex-col items-center gap-sm">
          <NotFoundIcon width={48} />
          <h1 className="font-bold text-md text-center">{titleText}</h1>
        </div>
        {subtitleText && <p className="mb-none text-center">{subtitleText}</p>}
        <Button
          data-testid="brand-no-results-button"
          onClick={onButtonClick ? onButtonClick : handleFavoriteClick}
          variant="tertiary"
          className="py-md px-xl w-full md:w-auto"
        >
          <span className="text-sm">{buttonText}</span>
        </Button>
        {linkText && linkHref && (
          <Link href={linkHref}>
            <span className="text-sm underline font-bold flex justify-center">
              {linkText}
            </span>
          </Link>
        )}
      </div>
      {isSsoModalVisible && (
        <SsoBumpoutModal
          closeModal={() => setIsSsoModalVisible(false)}
          trackSignUp={() => {
            setIsSsoModalVisible(false);
            publishEvent({
              action: Action.click,
              brandId: brandId,
              category: Category.brandPaid,
              label: "sign-up from follow",
            });
          }}
        />
      )}
    </div>
  );
};

export default ShopNoResults;
